import {
  I2cRadioButton,
  I2cCheckbox,
  I2cCheckboxGroup,
  I2cRadioGroup,
  I2cListItem,
  I2cInput,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { agentWebsiteLang, webRequire, dialingOptions, tollFreeOptions, resporgOptions } from "./stringUtils";
import { validationSchemaIVR } from "./ValidationSchema";
import { handleCheckboxArrayToggle, handleInputChange } from "./formUtils";

const IVRInteractiveVoiceResponse = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaIVR),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      ivr_would_you_like_ivr_services: "no",
      ivr_language: [],
      ivr_language_other: "",
      ivr_dialing_you_like_to_use_with_ivr: [],
      ivr_dialing_you_like_to_use_with_ivr_other: "",
      ivr_would_you_like_i2c_to_provide_toll_free_number_did: "no",
      ivr_would_you_like_to_resporg_your_toll_free_number_to_i2c: "no",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "ivr_would_you_like_ivr_services",
    "ivr_language",
    "ivr_language_other",
    "ivr_dialing_you_like_to_use_with_ivr",
    "ivr_dialing_you_like_to_use_with_ivr_other",
    "ivr_would_you_like_i2c_to_provide_toll_free_number_did",
    "ivr_would_you_like_to_resporg_your_toll_free_number_to_i2c",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      3: {
        ...addFormData["3"],
        "3_5": { ...values },
      },
    });
  }, [methods, setAddFormData, addFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[3]?.["3_5"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("3_5", true);
    } else {
      setFormValidStates("3_5", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const isWebRequired = methods.watch("ivr_would_you_like_ivr_services") === "yes";

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
            <div className="section-space">
              <div className="sub-heading mb-16">
                Would you like i2c to provide IVR Services?
              </div>
              <I2cRadioGroup
                label=""
                value={methods.watch("ivr_would_you_like_ivr_services")}
                name="ivr_would_you_like_ivr_services"
                onI2cChange={(e) => {
                  const value = e.target.value;
                  methods.setValue("ivr_would_you_like_ivr_services", value);

                  if (value !== "yes") {
                    methods.setValue("ivr_language", []);
                    methods.setValue("ivr_language_other", "");
                    methods.setValue("ivr_dialing_you_like_to_use_with_ivr", []);
                    methods.setValue("ivr_dialing_you_like_to_use_with_ivr_other", "");
                    methods.setValue("ivr_would_you_like_i2c_to_provide_toll_free_number_did", "no");
                    methods.setValue("ivr_would_you_like_to_resporg_your_toll_free_number_to_i2c", "");
                  }

                  methods.trigger();
                  handleValueChanges();
                }}
              >
                {webRequire.map((option) => (
                  <I2cRadioButton key={option.value} value={option.value}>
                    {option.label}
                  </I2cRadioButton>
                ))}
              </I2cRadioGroup>
              {errors.ivr_would_you_like_ivr_services && (
                <ErrorMessage message={errors.ivr_would_you_like_ivr_services.message} />
              )}
            </div>
            {isWebRequired && (
              <>
                <div className="section-space">
                  <div className="sub-heading mb-16">IVR Language</div>
                  <I2cCheckboxGroup key={`ivr-language-${methods.watch("ivr_would_you_like_ivr_services")}`}>
                    <div i2c-grid="grid gap-sm">
                      {agentWebsiteLang.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods.watch("ivr_language").includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleCheckboxArrayToggle(
                              methods,
                              "ivr_language",
                              web.value,
                              { dependentField: "ivr_language_other" },
                              handleValueChanges
                            )
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="ic_cAgentLang"
                            value={web.value}
                            label=""
                            filled
                            checked={methods.watch("ivr_language").includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.ivr_language && (
                    <ErrorMessage message={errors.ivr_language.message} />
                  )}
                  {methods.watch("ivr_language").includes("other") && (
                    <div i2c-grid="grid gap-sm" className="mt-16">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cInput
                            className="single-input-field"
                            placeholder="Please Specify"
                            size="large"
                            label="Enter Details"
                            name="ivr_language_other"
                            value={methods.watch("ivr_language_other")}
                            onI2cInput={(e) => 
                              handleInputChange(methods, "ivr_language_other", e.target.value, handleValueChanges)
                            }
                            invalid={errors.ivr_language_other ? true : false}
                          />
                          {errors.ivr_language_other && (
                            <ErrorMessage message={errors.ivr_language_other.message} />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="section-space">
                  <div className="sub-heading mb-16">
                    What Dialing would you like to use with the IVR?
                  </div>
                  <I2cCheckboxGroup key={`ivr-dialing-${methods.watch("ivr_would_you_like_ivr_services")}`}>
                    <div i2c-grid="grid gap-sm">
                      {dialingOptions.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods.watch("ivr_dialing_you_like_to_use_with_ivr").includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleCheckboxArrayToggle(
                              methods,
                              "ivr_dialing_you_like_to_use_with_ivr",
                              web.value,
                              { dependentField: "ivr_dialing_you_like_to_use_with_ivr_other" },
                              handleValueChanges
                            )
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="ic_cAgentLang"
                            value={web.value}
                            label=""
                            filled
                            checked={methods.watch("ivr_dialing_you_like_to_use_with_ivr").includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.ivr_dialing_you_like_to_use_with_ivr && (
                    <ErrorMessage message={errors.ivr_dialing_you_like_to_use_with_ivr.message} />
                  )}
                  {methods.watch("ivr_dialing_you_like_to_use_with_ivr").includes("other") && (
                    <div i2c-grid="grid gap-sm" className="mt-16">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cInput
                            className="single-input-field"
                            placeholder="Please Specify"
                            size="large"
                            label="Enter Details"
                            name="ivr_dialing_you_like_to_use_with_ivr_other"
                            value={methods.watch("ivr_dialing_you_like_to_use_with_ivr_other")}
                            onI2cInput={(e) => 
                              handleInputChange(methods, "ivr_dialing_you_like_to_use_with_ivr_other", e.target.value, handleValueChanges)
                            }
                            invalid={errors.ivr_dialing_you_like_to_use_with_ivr_other ? true : false}
                          />
                          {errors.ivr_dialing_you_like_to_use_with_ivr_other && (
                            <ErrorMessage message={errors.ivr_dialing_you_like_to_use_with_ivr_other.message} />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="section-space">
                  <div className="sub-heading mb-16">
                    Would you like i2c to provide the toll-free number/DID?
                  </div>
                  <I2cRadioGroup
                    label=""
                    value={methods.watch("ivr_would_you_like_i2c_to_provide_toll_free_number_did")}
                    name="ivr_would_you_like_i2c_to_provide_toll_free_number_did"
                    onI2cChange={(e) => {
                      const value = e.target?.value || e;
                      methods.setValue("ivr_would_you_like_i2c_to_provide_toll_free_number_did", value);
                      methods.trigger();
                      handleValueChanges();
                    }}
                  >
                    {tollFreeOptions.map((option) => (
                      <I2cRadioButton key={option.value} value={option.value}>
                        {option.label}
                      </I2cRadioButton>
                    ))}
                  </I2cRadioGroup>
                  {errors.ivr_would_you_like_i2c_to_provide_toll_free_number_did && (
                    <ErrorMessage message={errors.ivr_would_you_like_i2c_to_provide_toll_free_number_did.message} />
                  )}
                </div>

                <div className="section-space">
                  <div className="sub-heading mb-16">
                    Would you like to RESPORG your Toll Free number to i2c?
                  </div>
                  <I2cRadioGroup
                    label=""
                    name="ivr_would_you_like_to_resporg_your_toll_free_number_to_i2c"
                    value={methods.watch("ivr_would_you_like_to_resporg_your_toll_free_number_to_i2c")}
                    onI2cChange={(e) => {
                      const value = e.target?.value || e;
                      methods.setValue("ivr_would_you_like_to_resporg_your_toll_free_number_to_i2c", value);
                      methods.trigger();
                      handleValueChanges();
                    }}
                  >
                    {resporgOptions.map((option) => (
                      <I2cRadioButton key={option.value} value={option.value}>
                        {option.label}
                      </I2cRadioButton>
                    ))}
                  </I2cRadioGroup>
                  {errors.ivr_would_you_like_to_resporg_your_toll_free_number_to_i2c && (
                    <ErrorMessage message={errors.ivr_would_you_like_to_resporg_your_toll_free_number_to_i2c.message} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default IVRInteractiveVoiceResponse;
